import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import usePersistFn from '@/hooks/usePersistFn';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import type { NavbarItem, NavbarMenuItem } from '@/shared/constant/navbar/NavbarItem';
import { NavbarItemTypeEnum } from '@/shared/constant/navbar/NavbarItem';
import isEqual from 'lodash-es/isEqual';
import React from 'react';

export function useNavbarGroupShowCount(items: NavbarItem[]) {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const [containerElement, getContainerRef] = React.useState<HTMLElement | null>(null);
  const [itemList, setItemList] = React.useState<{
    showList: NavbarItem[];
    moreList: NavbarItem[];
  }>(() => {
    return {
      showList: items,
      moreList: [],
    };
  });
  const childElementWidthCacheRef = React.useRef<number[] | undefined>(undefined);
  const getChildElementWidth = usePersistFn((ele: HTMLElement) => {
    childElementWidthCacheRef.current ??= [...ele.childNodes].map((x) => {
      if ('offsetWidth' in x && typeof x.offsetWidth === 'number') {
        return x.offsetWidth + 8;
      }

      return 8;
    });

    return childElementWidthCacheRef.current;
  });

  const checkElement = usePersistFn((ele: HTMLElement, width: number) => {
    const childElementWidthList = getChildElementWidth(ele);
    const newItems = [...items];

    const selectedIndex = newItems.findIndex(
      (x) => x.type !== NavbarItemTypeEnum.Link && x.selected,
    );

    const showList: NavbarItem[] = [];
    const moreList: NavbarItem[] = [];

    let totalWidth = childElementWidthList[selectedIndex] ?? 0;
    let showListIncludeSelected = true;
    let selectedItem: NavbarItem | undefined = undefined;
    newItems.forEach((x, i) => {
      const selected = i === selectedIndex;

      const currentWidth = selected ? 0 : (childElementWidthList[i] ?? 0);
      totalWidth += currentWidth;

      if (totalWidth > width - 70) {
        if (selected) {
          showListIncludeSelected = false;
          selectedItem = x;
        } else {
          moreList.push(x);
        }
      } else {
        showList.push(x);
      }
    });

    if (!showListIncludeSelected && selectedItem) {
      showList.push(selectedItem);
    }

    setItemList((oldValue) => {
      const newValue = {
        showList,
        moreList,
      };

      if (!isEqual(oldValue, newValue)) {
        return newValue;
      }

      return oldValue;
    });
  });

  const widthCacheRef = React.useRef(-1);
  useIsomorphicLayoutEffect(() => {
    let observer: ResizeObserver | undefined = undefined;

    if (containerElement) {
      childElementWidthCacheRef.current = undefined;
      widthCacheRef.current = -1;
      observer = new ResizeObserver(([entry]) => {
        const contentRect = entry?.contentRect;
        if (contentRect) {
          if (
            (contentRect.width === 0 && contentRect.height === 0) ||
            widthCacheRef.current === contentRect.width
          ) {
            return;
          }
          widthCacheRef.current = contentRect.width;

          checkElement(containerElement, contentRect.width);
        }
      });
      observer.observe(containerElement);
    }

    return () => {
      observer?.disconnect();
    };
  }, [containerElement, items]);

  const moreMenuItem = React.useMemo(() => {
    if (!itemList.moreList.length) {
      return undefined;
    }

    const result: NavbarMenuItem = {
      children: itemList.moreList,
      disabled: false,
      icon: undefined,
      id: 'more.0',
      selected: false,
      title: clientI18n('more'),
      type: NavbarItemTypeEnum.Menu,
    };

    return result;
  }, [itemList.moreList, clientI18n]);

  return {
    getContainerRef,
    showList: itemList.showList,
    moreMenuItem,
  };
}
